/**
 * Print/PDF pagination rules — scoped to the encounter template root so every
 * form-type template (Psychodynamic, SOAP, etc.) inherits the same behavior.
 *
 * Strategy used (standard CSS Fragmentation, consumed by html2pdf.js / browser
 * print engines):
 *   1. Mark every sub-section card (a wrapper div whose first child is the
 *      styled SectionHead paragraph) as `page-break-inside: avoid`. The engine
 *      then pushes the whole card to the next page rather than slicing it
 *      mid-content. A small gap at the bottom of the previous page is the
 *      accepted trade-off; data is never split mid-word.
 *
 *      The exceptions are the top-level bands (`.report-section`) and the cards
 *      whose height follows the data (`.report-card--flow`, e.g. Prescription
 *      and CPT Code). html2pdf pushes any `break-inside: avoid` element that
 *      straddles a page boundary and is at most one page tall onto the next
 *      page, so marking a band that wraps a whole Plan — or a list that grows
 *      with the record — left up to a full page of white space behind it. Those
 *      continue across the break instead; their leaf rows stay atomic, so a
 *      line is still never split.
 *   2. Keep small leaf elements (<p>, <li>) atomic for the same reason at the
 *      sub-section level.
 *   3. Glue section headers to the first content row via `break-after: avoid`.
 *   4. Top/bottom padding on the root gives breathing room near page edges so
 *      content does not visually butt against the cut line.
 *
 * Note: `box-decoration-break: clone` was intentionally not used here. While
 * useful in theory for split sections, it caused empty bordered boxes to
 * appear at the top of pages whenever a parent wrapper spanned a page break
 * but its child content had already been pushed forward.
 */
export const ENCOUNTER_PDF_PRINT_STYLES = `
  .encounter-pdf-root {
    padding-top: 12px;
    padding-bottom: 12px;
    orphans: 4;
    widows: 4;
  }
  /* Section cards: any div whose direct child is a styled SectionHead <p>,
     except the top-level bands and the data-driven lists. Those flow across
     pages instead of being bumped whole to the next one, which is what left
     the report with large blank areas before the Prescription and CPT Code
     blocks. The exclusions live in this selector rather than in a later
     override because the :has() selector outweighs a plain class rule. */
  .encounter-pdf-root
    div:has(> p[style*="background"]):not(.report-section):not(.report-card--flow) {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .encounter-pdf-root .report-section,
  .encounter-pdf-root .report-card--flow {
    page-break-inside: auto;
    break-inside: auto;
  }
  /* SubSection / NestedText outer wrappers (identified by border-radius in
     inline style). Required because their overflow:hidden creates a new
     block-formatting context that traps the inner card's break-inside rule
     and lets the outer border render on the previous page as an empty box. */
  .encounter-pdf-root div[style*="border-radius"] {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  /* Atomic leaf rows: key-value lines, list items, header paragraphs */
  .encounter-pdf-root p,
  .encounter-pdf-root li {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  /* Section header glued to first content row */
  .encounter-pdf-root p[style*="background"] {
    page-break-after: avoid;
    break-after: avoid;
  }
  .encounter-pdf-root h1,
  .encounter-pdf-root h2,
  .encounter-pdf-root h3,
  .encounter-pdf-root h4,
  .encounter-pdf-root h5,
  .encounter-pdf-root h6 {
    page-break-after: avoid;
    break-after: avoid;
  }
  @media print {
    .encounter-pdf-root
      div:has(> p[style*="background"]):not(.report-section):not(.report-card--flow) {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .encounter-pdf-root .report-section,
    .encounter-pdf-root .report-card--flow {
      page-break-inside: auto;
      break-inside: auto;
    }
  }
`;
